import { html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { parseEmbedTrack } from "../../libs/client/embedTracking";
import { TrackingElement } from "./TrackingElement";

@customElement("survaq-banner-modal")
class SurvaqBannerModal extends TrackingElement {
  @state() private isOpen = false;

  @property({ type: Boolean, attribute: "auto-open" }) autoOpen = false;
  @property({ type: Boolean, attribute: "hide-close-button" }) hideCloseButton = false;
  @property({ type: Boolean, attribute: "disable-overlay-close" }) disableOverlayClose = false;

  private previouslyFocusedElement: HTMLElement | null = null;

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("message", this.handleEmbedMessage);
    if (this.autoOpen) {
      this.openModal();
    }
  }

  disconnectedCallback() {
    window.removeEventListener("message", this.handleEmbedMessage);
    super.disconnectedCallback();
  }

  /**
   * 中に埋めた iframe (クーポン面など) からの操作の知らせを、親ページの GA に送る。
   * 受け取るのは、このモーダルの content に入っている iframe からのものだけ。
   */
  private handleEmbedMessage = (e: MessageEvent) => {
    const track = parseEmbedTrack(e.data);
    if (!track) return;
    const fromOwnIframe = Array.from(this.querySelectorAll("iframe")).some(
      (iframe) => iframe.contentWindow !== null && iframe.contentWindow === e.source,
    );
    if (fromOwnIframe) this.trackAction(track.action, track.label);
  };

  render() {
    return html`
      ${this.renderTopMarker()}
      ${
        this.sneak || this.autoOpen
          ? html``
          : html`<button @click="${this.openModal}">
            <slot name="button"></slot>
          </button>`
      }
      ${this.renderBottomMarker()}
      <div
        class="fixed inset-0 bg-black/60 flex items-center justify-center z-20 p-4
               ${this.isOpen ? "block" : "hidden"}"
        ?hidden="${!this.isOpen}"
        role="dialog"
        aria-modal="true"
        @click="${this.handleOverlayClick}"
        @keydown="${this.handleKeydown}"
      >
        <div class="relative flex flex-col max-w-[600px] w-fit max-h-[calc(100dvh-4rem)]">
          <div class="absolute -top-5 -right-5 z-20">
            <button
              class="flex items-center justify-center text-center size-10 text-white bg-black rounded-full text-xl"
              aria-label="閉じる"
              @click="${this.closeModal}"
            >
              ×
            </button>
          </div>
          <div
            id="modal-content"
            class="space-y-1 flex flex-col overflow-y-auto overflow-x-hidden"
            @click="${this.stopPropagation}"
          >
            <slot name="content"></slot>
            ${
              this.hideCloseButton
                ? html``
                : html`<button
                  id="close-button"
                  @click="${this.closeModal}"
                  class="bg-transparent font-semibold w-full text-white underline shrink-0"
                >
                  とじる
                </button>`
            }
          </div>
        </div>
      </div>
    `;
  }

  private openModal() {
    this.previouslyFocusedElement = document.activeElement as HTMLElement;
    this.isOpen = true;
    // auto-openの場合はスクロールロックをスキップ
    // 理由: survaq-conditional-elementなどで非表示にされている間もconnectedCallbackが
    // 呼ばれるため、表示前にスクロールロックがかかってしまう問題を回避する
    if (!this.autoOpen) {
      document.body.style.overflow = "hidden";
    }
    this.updateComplete.then(() => {
      // スロットコンテンツのレンダリング後にスクロールリセット
      requestAnimationFrame(() => {
        const modalContent = this.shadowRoot?.querySelector("#modal-content");
        if (modalContent) {
          modalContent.scrollTop = 0;
        }
      });

      // preventScroll は必須。「とじる」は中身の最下部にあるので、素で focus すると
      // ブラウザがそこまでスクロールしてモーダルが一番下から開く。上の scrollTop = 0 は
      // それを戻そうとしているが、rAF と focus のスクロールが競合して間に合わないことがある
      // (実測: 画像版は5/5、HTML版は5回中3回が最下部から開いた)。競合させず原因側を消す。
      const closeButton = this.shadowRoot?.querySelector<HTMLButtonElement>("#close-button");
      if (!this.hideCloseButton && closeButton) {
        closeButton.focus({ preventScroll: true });
      } else {
        // フォーカス可能な要素にフォーカスを設定
        const focusableElements = this.getFocusableElements();
        focusableElements[0]?.focus({ preventScroll: true });
      }
    });
  }

  private closeModal() {
    this.isOpen = false;
    document.body.style.overflow = "";
    this.previouslyFocusedElement?.focus();
  }

  private handleOverlayClick(e: Event) {
    if (!this.disableOverlayClose && e.target === e.currentTarget) {
      this.closeModal();
    }
  }

  private handleKeydown(e: KeyboardEvent) {
    if (e.key === "Escape") {
      this.closeModal();
    }
    if (e.key === "Tab") {
      this.trapFocus(e);
    }
  }

  private trapFocus(e: KeyboardEvent) {
    const focusableElements = this.getFocusableElements();
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    if (!firstElement || !lastElement) return;

    if (e.shiftKey && document.activeElement === firstElement) {
      e.preventDefault();
      lastElement.focus();
    } else if (!e.shiftKey && document.activeElement === lastElement) {
      e.preventDefault();
      firstElement.focus();
    }
  }

  private getFocusableElements(): HTMLElement[] {
    const modal = this.shadowRoot?.querySelector('[role="dialog"]');
    if (!modal) return [];

    const focusableSelectors = [
      "button:not([disabled])",
      "a[href]",
      "input:not([disabled])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      '[tabindex]:not([tabindex="-1"])',
    ].join(", ");

    const elements = Array.from(modal.querySelectorAll<HTMLElement>(focusableSelectors));

    // スロット内の要素も取得
    const slots = modal.querySelectorAll("slot");
    slots.forEach((slot) => {
      const assignedElements = slot.assignedElements({ flatten: true });
      assignedElements.forEach((el) => {
        if (el instanceof HTMLElement) {
          if (el.matches(focusableSelectors)) {
            elements.push(el);
          }
          const nestedFocusable = el.querySelectorAll<HTMLElement>(focusableSelectors);
          elements.push(...Array.from(nestedFocusable));
        }
      });
    });

    return elements.filter((el) => el.offsetParent !== null);
  }

  private stopPropagation(e: Event) {
    e.stopPropagation();
  }
}

export { SurvaqBannerModal };
